import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ACTIONS from "../../../../redux/action";
import axios from "../../../../shared/axios/axiosProxy";
import i18n from "../../../../utils/i18n";
import RevenueSlider from "../../components/revenueSlider/RevenueSlider.jsx";
import classes from "./companyEmployeeNo.module.scss";

const MAX_EMPLOYEES = 250;
const CUSTOM_MODE_THRESHOLD = 10;
const DEFAULT_EMPLOYEES = 0;

const EMPLOYEE_SLIDER_STEPS = Array.from({ length: 11 }, (_, index) => ({
  value: index,
  display: index === CUSTOM_MODE_THRESHOLD ? "10+" : String(index),
}));

const parseEmployeeCount = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const getSliderIndexForCount = (count) =>
  count >= CUSTOM_MODE_THRESHOLD
    ? CUSTOM_MODE_THRESHOLD
    : Math.min(Math.max(0, count), CUSTOM_MODE_THRESHOLD - 1);

const COMPANY_TYPES_PAYROLL_GT1 = [
  "kiva",
  "singlesecondary",
  "singleprimary",
  "vszja",
];
const COMPANY_TYPES_PAYROLL_GT0 = ["bt", "kft", "zrt"];

const normalizeServices = (services) => {
  if (Array.isArray(services)) return services;
  if (typeof services === "string" && services.trim()) {
    return services
      .split(",")
      .map((service) => service.trim())
      .filter(Boolean);
  }
  return [];
};

export const isCompanyEmployeeNoValid = (noofemployees) => {
  const parsed = parseEmployeeCount(noofemployees);
  return parsed !== null && parsed <= MAX_EMPLOYEES;
};

const CompanyEmployeeNo = () => {
  const dispatch = useDispatch();
  const { noofemployees, noofemployeesSource, searchID, accounting, companytype, services } =
    useSelector((state) => state.company_store);

  const storeEmployeeCount =
    parseEmployeeCount(noofemployees) ?? DEFAULT_EMPLOYEES;

  const sliderMarks = useMemo(
    () => EMPLOYEE_SLIDER_STEPS.map((_, index) => ({ value: index })),
    [],
  );

  const [sliderIndex, setSliderIndex] = useState(() =>
    getSliderIndexForCount(storeEmployeeCount),
  );
  const [customInput, setCustomInput] = useState(() =>
    storeEmployeeCount >= CUSTOM_MODE_THRESHOLD
      ? String(storeEmployeeCount)
      : "0",
  );
  const [employeesError, setEmployeesError] = useState(false);

  const customInputRef = useRef(null);
  const pendingCustomInputScrollRef = useRef(false);

  const isCustomEmployeeMode =
    sliderIndex >= CUSTOM_MODE_THRESHOLD ||
    storeEmployeeCount >= CUSTOM_MODE_THRESHOLD;

  const currentStep = EMPLOYEE_SLIDER_STEPS[sliderIndex];
  const displayValue = currentStep?.display ?? "0";

  const shouldSetPayrollFlag = useCallback(
    (count) => {
      if (!accounting) return false;
      if (count > 1 && COMPANY_TYPES_PAYROLL_GT1.includes(companytype)) {
        return true;
      }
      if (count > 0 && COMPANY_TYPES_PAYROLL_GT0.includes(companytype)) {
        return true;
      }
      return false;
    },
    [accounting, companytype],
  );

  const syncPayrollServiceInStoreAndSearch = useCallback(
    (count, patchSearch) => {
      const currentServices = normalizeServices(services);
      const hasPayrollService = currentServices.includes("payroll");
      const shouldContainPayrollService = count >= 1;

      if (shouldContainPayrollService === hasPayrollService) return;

      const updatedServices = shouldContainPayrollService
        ? [...currentServices, "payroll"]
        : currentServices.filter((service) => service !== "payroll");

      dispatch(
        ACTIONS.addCompanyInfo({
          id: "services",
          data: updatedServices,
          stateObject: "company_store",
        }),
      );
      patchSearch({ services: updatedServices });
    },
    [dispatch, services],
  );

  const applyPayrollSideEffects = useCallback(
    (count, patchSearch) => {
      if (shouldSetPayrollFlag(count)) {
        dispatch(
          ACTIONS.addCompanyInfo({
            id: "payroll",
            data: true,
            stateObject: "company_store",
          }),
        );
      }
      syncPayrollServiceInStoreAndSearch(count, patchSearch);
    },
    [dispatch, shouldSetPayrollFlag, syncPayrollServiceInStoreAndSearch],
  );

  const saveEmployeeCount = useCallback(
    (rawCount, source = "user") => {
      const numeric = Number(rawCount);
      if (Number.isNaN(numeric)) {
        return false;
      }

      if (numeric > MAX_EMPLOYEES) {
        setEmployeesError(true);
        return false;
      }

      setEmployeesError(false);
      setSliderIndex(getSliderIndexForCount(numeric));
      if (numeric < CUSTOM_MODE_THRESHOLD) {
        setCustomInput("0");
      } else {
        setCustomInput(String(numeric));
      }

      dispatch(
        ACTIONS.addCompanyInfo({
          id: "noofemployees",
          data: numeric,
          stateObject: "company_store",
        }),
      );
      dispatch(
        ACTIONS.addCompanyInfo({
          id: "noofemployeesSource",
          data: source,
          stateObject: "company_store",
        }),
      );

      const patchSearch = (body) => {
        if (!searchID) {
          return;
        }
        axios
          .patch(`/api/search/${searchID}`, body, {
            headers: { "Content-Type": "application/json" },
          })
          .catch((error) => {
            console.error(
              "Error saving employee step data to searchSchema:",
              error.response?.data || error.message,
            );
          });
      };

      patchSearch({ noofemployees: numeric });
      applyPayrollSideEffects(numeric, patchSearch);
      return true;
    },
    [applyPayrollSideEffects, dispatch, searchID],
  );

  const didInitializeRef = useRef(false);
  useEffect(() => {
    if (!didInitializeRef.current) {
      didInitializeRef.current = true;
      const parsed = parseEmployeeCount(noofemployees);
      // Nothing stored yet is a genuine default; re-persisting an existing
      // value (e.g. from Cégjelző) on mount is just normalization, not a new
      // user choice — keep its existing source tag. `parsed === 0` is
      // indistinguishable from "never touched" (DEFAULT_EMPLOYEES is also 0),
      // so treat a still-unset source the same as no stored value at all.
      saveEmployeeCount(
        parsed ?? DEFAULT_EMPLOYEES,
        parsed === null || !noofemployeesSource ? "default" : noofemployeesSource,
      );
      return;
    }

    const parsed = parseEmployeeCount(noofemployees);
    if (parsed === null) {
      return;
    }

    if (parsed !== Number(noofemployees)) {
      saveEmployeeCount(parsed, noofemployeesSource || "default");
    } else {
      setSliderIndex(getSliderIndexForCount(parsed));
      setCustomInput(
        parsed >= CUSTOM_MODE_THRESHOLD ? String(parsed) : "0",
      );
    }
  }, [noofemployees, noofemployeesSource, saveEmployeeCount]);

  useEffect(() => {
    if (!isCustomEmployeeMode || !pendingCustomInputScrollRef.current) {
      return;
    }

    pendingCustomInputScrollRef.current = false;
    const frame = requestAnimationFrame(() => {
      customInputRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      customInputRef.current?.focus({ preventScroll: true });
    });

    return () => cancelAnimationFrame(frame);
  }, [isCustomEmployeeMode, sliderIndex]);

  const commitIndex = useCallback(
    (index) => {
      const enteringCustomMode =
        index >= CUSTOM_MODE_THRESHOLD && sliderIndex < CUSTOM_MODE_THRESHOLD;

      if (index >= CUSTOM_MODE_THRESHOLD) {
        const parsedCustom = Number(customInput);
        const nextValue =
          parsedCustom >= CUSTOM_MODE_THRESHOLD && !Number.isNaN(parsedCustom)
            ? parsedCustom
            : CUSTOM_MODE_THRESHOLD;
        saveEmployeeCount(nextValue);
        if (enteringCustomMode) {
          setCustomInput("0");
          pendingCustomInputScrollRef.current = true;
        }
        return;
      }
      saveEmployeeCount(index);
    },
    [customInput, saveEmployeeCount, sliderIndex],
  );

  const handleSliderChange = useCallback((_, value) => {
    setSliderIndex(Number(value));
  }, []);

  const handleSliderCommit = useCallback(
    (_, value) => commitIndex(Number(value)),
    [commitIndex],
  );

  const handleStepChange = useCallback(
    (delta) => {
      const nextIndex = Math.min(
        EMPLOYEE_SLIDER_STEPS.length - 1,
        Math.max(0, sliderIndex + delta),
      );
      commitIndex(nextIndex);
    },
    [commitIndex, sliderIndex],
  );

  const handleCustomInputChange = useCallback((event) => {
    const value = event.target.value;
    setCustomInput(value);

    if (value === "") {
      setEmployeesError(false);
      return;
    }

    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return;
    }

    setEmployeesError(numeric > MAX_EMPLOYEES);
  }, []);

  const handleCustomInputFocus = useCallback((event) => {
    event.target.select();
  }, []);

  const handleCustomInputBlur = useCallback(() => {
    if (customInput === "" || customInput === "0") {
      setCustomInput("0");
      saveEmployeeCount(CUSTOM_MODE_THRESHOLD);
      return;
    }

    const numeric = Number(customInput);
    if (Number.isNaN(numeric)) {
      setCustomInput("0");
      saveEmployeeCount(CUSTOM_MODE_THRESHOLD);
      return;
    }

    if (numeric > MAX_EMPLOYEES) {
      setEmployeesError(true);
      return;
    }

    const value =
      sliderIndex >= CUSTOM_MODE_THRESHOLD && numeric < CUSTOM_MODE_THRESHOLD
        ? CUSTOM_MODE_THRESHOLD
        : numeric;
    saveEmployeeCount(value);
  }, [customInput, saveEmployeeCount, sliderIndex]);

  return (
    <div className={classes.root}>
      <div className={classes.scrollArea}>
        <div className={classes.content}>
          <div className={classes.mainColumn}>
            <header className={classes.header}>
              <h1 className={classes.title}>
                {i18n.t("searchflow.employeetitle")}
              </h1>
              <p className={classes.subtitle}>
                {i18n.t("searchflow.employeesubtitle")}
              </p>
            </header>

            <div className={classes.rightColumn}>
              <div className={classes.body}>
                <div className={classes.controls}>
                  <div className={classes.valueRow}>
                    <button
                      type="button"
                      className={classes.stepButton}
                      aria-label={i18n.t("searchflow.employeeprevaria")}
                      onClick={() => handleStepChange(-1)}
                      disabled={sliderIndex <= 0}
                    >
                      −
                    </button>

                    <div className={classes.valueDisplay}>
                      <div className={classes.valueRange}>{displayValue}</div>
                      <div className={classes.valueUnit}>{i18n.t("searchflow.embersuffix")}</div>
                    </div>

                    <button
                      type="button"
                      className={classes.stepButton}
                      aria-label={i18n.t("searchflow.employeenextaria")}
                      onClick={() => handleStepChange(1)}
                      disabled={sliderIndex >= EMPLOYEE_SLIDER_STEPS.length - 1}
                    >
                      +
                    </button>
                  </div>

                  <RevenueSlider
                    value={sliderIndex}
                    max={EMPLOYEE_SLIDER_STEPS.length - 1}
                    marks={sliderMarks}
                    onChange={handleSliderChange}
                    onChangeCommitted={handleSliderCommit}
                    ariaLabel={i18n.t("general.noofemployeeslabel")}
                    startLabel={i18n.t("searchflow.employeestartlabel")}
                    endLabel={i18n.t("searchflow.employeeendlabel")}
                  />
                </div>

                {isCustomEmployeeMode ? (
                  <div className={classes.customSection}>
                    <div className={classes.customField}>
                      <p className={classes.customPrompt} id="noofemployees-prompt">
                        {i18n.t("searchflow.employeecustomprompt")}
                      </p>
                      <input
                        ref={customInputRef}
                        id="noofemployees"
                        name="noofemployees"
                        type="number"
                        className={`${classes.customInput} ${
                          employeesError ? classes.customInputError : ""
                        }`}
                        value={customInput}
                        onChange={handleCustomInputChange}
                        onFocus={handleCustomInputFocus}
                        onBlur={handleCustomInputBlur}
                        placeholder={i18n.t("general.noofemployeeslabel")}
                        aria-labelledby="noofemployees-prompt"
                        min={CUSTOM_MODE_THRESHOLD}
                        inputMode="numeric"
                      />
                    </div>
                    {employeesError ? (
                      <p className={classes.errorMessage}>
                        {i18n.t("providerdashboard.pricebuttonmax")} {MAX_EMPLOYEES} {i18n.t("searchflow.maxemployeessuffix")}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyEmployeeNo;
